import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Check, Plus, Truck, ShieldCheck, Lock, ArrowRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import {
  PRODUCT, PHOTO_REVIEWS, BEFORE_AFTER, STATS, SCIENCE,
  FEATURE_BENEFITS, PRESS_LOGOS, LIFESTYLE, FAQS, VC_BANNER, HERO_VIDEO, BUNDLES,
} from "../mock";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import VideoSlider from "../components/VideoSlider";

const money = (n) => `$${n.toFixed(2)}`;

const Stars = ({ size = 15 }) => (
  <div className="flex gap-0.5 text-[#1A1A1A]">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </div>
);

const Product = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [bundle, setBundle] = useState(BUNDLES[0]);
  const reviewsRef = useRef(null);
  const media = [
    { type: "video", src: PRODUCT.video },
    ...PRODUCT.images.map((src) => ({ type: "image", src })),
  ];

  const scrollReviews = (dir) => {
    const el = reviewsRef.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.85, 640);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  const handleAdd = () => {
    addToCart(bundle);
    toast.success("Added to your cart");
  };

  const handleBuyNow = () => {
    addToCart(bundle, false);
    navigate("/checkout");
  };

  return (
    <main className="bg-[#F4EFE8]">
      {/* GALLERY + BUY BOX */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-8 md:py-12 grid md:grid-cols-2 gap-8 lg:gap-14">
        {/* Gallery */}
        <div className="md:sticky md:top-28 self-start">
          <div className="bg-white rounded-sm overflow-hidden">
            {media[active].type === "video" ? (
              <video
                key={media[active].src}
                className="w-full aspect-square object-cover bg-black"
                autoPlay
                loop
                muted
                playsInline
                controls
                poster={PRODUCT.images[0]}
                src={media[active].src}
              />
            ) : (
              <img src={media[active].src} alt={PRODUCT.title} className="w-full aspect-square object-cover" />
            )}
          </div>
          <div className="grid grid-cols-6 gap-2 mt-3">
            {media.slice(0, 6).map((m, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`relative bg-white rounded-sm overflow-hidden border transition-all ${
                  active === i ? "border-[#1A1A1A]" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                {m.type === "video" ? (
                  <>
                    <img src={PRODUCT.images[0]} alt="" className="w-full aspect-square object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                      <Play size={16} className="text-white" fill="currentColor" />
                    </span>
                  </>
                ) : (
                  <img src={m.src} alt="" className="w-full aspect-square object-cover" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Buy box */}
        <div className="pt-2">
          <div className="flex items-center gap-3 mb-3">
            <Stars />
            <span className="text-[13px] text-[#1A1A1A]/60">4.9 / 5 &nbsp; Loved by {PRODUCT.lovedBy} customers</span>
          </div>
          <div className="inline-flex items-center gap-2 mb-4 bg-[#6D28D9]/10 text-[#6D28D9] px-3 py-1.5 rounded-full text-[11px] tracking-[0.14em] uppercase font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-[#6D28D9]" />
            VC-Backed &bull; Developed by 4 Dermatologists
          </div>
          <h1 className="font-serif-display text-[32px] md:text-[42px] leading-[1.08] font-medium mb-4">
            {PRODUCT.title}
          </h1>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[26px] font-medium">{money(PRODUCT.price)}</span>
            <span className="text-[18px] line-through text-[#1A1A1A]/40">{money(PRODUCT.compareAt)}</span>
            <span className="bg-[#1A1A1A] text-[#F4EFE8] text-[10px] tracking-[0.14em] uppercase px-3 py-1.5">
              {PRODUCT.discountLabel}
            </span>
          </div>

          <div className="space-y-4 border-y border-[#1A1A1A]/12 py-6 mb-6">
            {PRODUCT.highlights.map((h) => (
              <div key={h.title} className="flex gap-3">
                <Check size={18} className="text-[#8a6d3b] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[14px] font-medium leading-snug">{h.title}</p>
                  <p className="text-[13px] text-[#1A1A1A]/60">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between text-[12px] tracking-[0.08em] uppercase text-[#1A1A1A]/60 mb-3">
            <span className="text-[#8a6d3b]">{PRODUCT.orderedThisMonth} Ordered This Month</span>
            <span>{PRODUCT.shipsBy}</span>
          </div>

          {/* Low-stock urgency */}
          <div className="mb-5">
            <div className="flex items-center justify-between text-[12px] mb-1.5">
              <span className="font-semibold text-[#b91c1c] tracking-[0.04em] uppercase">
                Only {PRODUCT.stockLeft} left in stock
              </span>
              <span className="text-[#1A1A1A]/55">Next release: {PRODUCT.nextRelease}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-[#1A1A1A]/10 overflow-hidden">
              <div className="h-full rounded-full bg-[#b91c1c]" style={{ width: "18%" }} />
            </div>
          </div>

          {/* Bundle selector — 1 / 2 / 3 masks, each maps to its own price + link */}
          <div className="grid grid-cols-3 gap-2.5 mb-4" data-testid="bundle-selector">
            {BUNDLES.map((b) => {
              const selected = bundle.qty === b.qty;
              return (
                <button
                  key={b.qty}
                  onClick={() => setBundle(b)}
                  data-testid={`bundle-option-${b.qty}`}
                  className={`relative text-left rounded-sm border p-3.5 transition-all ${
                    selected
                      ? "border-[#1A1A1A] bg-white shadow-sm"
                      : "border-[#1A1A1A]/20 bg-transparent hover:border-[#1A1A1A]/50"
                  }`}
                >
                  {b.tag && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#6D28D9] text-white text-[9px] tracking-[0.1em] uppercase px-2 py-0.5 rounded-full">
                      {b.tag}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 mb-1">
                    <span className={`h-3.5 w-3.5 rounded-full border flex items-center justify-center ${selected ? "border-[#1A1A1A]" : "border-[#1A1A1A]/40"}`}>
                      {selected && <span className="h-1.5 w-1.5 rounded-full bg-[#1A1A1A]" />}
                    </span>
                    <span className="text-[13px] font-medium">{b.label}</span>
                  </span>
                  <span className="block text-[17px] font-medium leading-none">{money(b.price)}</span>
                  <span className="block text-[10px] text-[#1A1A1A]/50 mt-1">{b.sub}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={handleAdd}
            data-testid="add-to-cart-btn"
            className="btn-lux w-full py-4 text-[12px] tracking-[0.2em] uppercase mb-3"
          >
            Add to cart
          </button>
          <button
            onClick={handleBuyNow}
            data-testid="buy-now-btn"
            className="btn-lux w-full py-4 text-[12px] tracking-[0.22em] uppercase gap-2 mb-6"
          >
            <Lock size={13} /> Buy It Now
          </button>

          {/* Big benefit bullets */}
          <ul className="space-y-4">
            {PRODUCT.buyBullets.map((b) => (
              <li key={b} className="flex items-center gap-3.5">
                <span className="h-7 w-7 rounded-full border border-[#6D28D9]/50 text-[#6D28D9] flex items-center justify-center flex-shrink-0">
                  <Check size={15} strokeWidth={1.25} />
                </span>
                <span className="text-[16px] md:text-[18px] font-light tracking-wide text-[#1A1A1A]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FLOWLUX IN ACTION (under the product) */}
      <VideoSlider />

      {/* MINI REVIEWS ROW */}
      <section className="bg-[#6D28D9] text-white overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee-fast text-[11px] tracking-[0.22em] uppercase font-semibold py-3">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="px-8 inline-flex items-center">{VC_BANNER}</span>
          ))}
        </div>
      </section>
      <section className="py-16 md:py-20 bg-[#EDE6DC] border-b border-[#1A1A1A]/10" id="reviews-carousel">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <p className="text-center text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-3">Red Light Works!</p>
          <h2 className="font-serif-display text-[32px] md:text-[46px] font-medium text-center mb-3">
            Clear Your Skin Today!
          </h2>
          <p className="text-center text-[13px] text-[#1A1A1A]/60 mb-12">4.9/5 from 1,500 reviews</p>
          <div className="relative">
            <div
              ref={reviewsRef}
              className="flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth pb-2"
            >
              {PHOTO_REVIEWS.map((r) => (
                <div
                  key={r.name}
                  className="snap-start shrink-0 w-[280px] sm:w-[320px] bg-[#F4EFE8] rounded-sm overflow-hidden"
                >
                  <img src={r.img} alt={r.name} className="w-full aspect-square object-cover" />
                  <div className="p-5">
                    <Stars size={12} />
                    <p className="text-[13px] text-[#1A1A1A]/75 leading-relaxed italic mt-3 mb-3">&ldquo;{r.text}&rdquo;</p>
                    <p className="text-[12px] font-medium tracking-wide">{r.name}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => scrollReviews(-1)}
              aria-label="Previous reviews"
              className="btn-lux hidden md:flex absolute -left-4 top-[38%] h-11 w-11 rounded-full"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollReviews(1)}
              aria-label="Next reviews"
              className="btn-lux hidden md:flex absolute -right-4 top-[38%] h-11 w-11 rounded-full"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="flex md:hidden items-center justify-center gap-4 mt-6">
            <button onClick={() => scrollReviews(-1)} aria-label="Previous" className="btn-lux h-11 w-11 rounded-full">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => scrollReviews(1)} aria-label="Next" className="btn-lux h-11 w-11 rounded-full">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* SHOP CONFIDENCE + AS SEEN IN */}
      <section className="py-16 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif-display text-[34px] md:text-[44px] font-medium mb-4">Shop with Total Confidence</h2>
          <p className="text-[14px] text-[#1A1A1A]/70 mb-8">Red Light Therapy is clinically backed by Dermatologists in the U.S.!</p>
          <div className="inline-flex flex-col gap-2.5 text-left">
            {["FREE - 5 Day U.S. Shipping!", "30 Day Money Back Guarantee!", "Complete Glow Up with Every Mask"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <Check size={16} className="text-[#8a6d3b]" /><span className="text-[14px]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="pb-16 overflow-hidden">
        <p className="text-center text-[11px] tracking-[0.3em] uppercase text-[#1A1A1A]/50 mb-6">As Seen In</p>
        <div className="flex whitespace-nowrap animate-marquee-fast items-center">
          {[...PRESS_LOGOS, ...PRESS_LOGOS, ...PRESS_LOGOS, ...PRESS_LOGOS].map((l, i) => (
            <img key={i} src={l} alt="press" className="h-8 md:h-10 w-auto mx-10 opacity-60 grayscale object-contain" />
          ))}
        </div>
      </section>

      {/* BEFORE & AFTER */}
      <section id="reviews" className="py-16 bg-[#EDE6DC]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <h2 className="font-serif-display text-[30px] md:text-[42px] font-medium text-center mb-12">
            Our Customers Before &amp; Afters
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BEFORE_AFTER.map((b) => (
              <div key={b.who} className="bg-[#F4EFE8] rounded-sm overflow-hidden">
                <img src={b.img} alt={b.who} className="w-full aspect-[3/4] object-cover" />
                <div className="p-5">
                  <p className="text-[14px] font-medium mb-1">{b.title}</p>
                  <p className="text-[12px] text-[#1A1A1A]/55">
                    <span className="font-medium text-[#1A1A1A]">{b.who}</span>, {b.detail}
                  </p>
                  <p className="text-[11px] text-[#8a6d3b] mt-2">FlowLux Verified &#10003;</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCIENCE / STATS */}
      <section id="science" className="py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <p className="text-center text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-3">The Science of Visible Results</p>
          <h2 className="font-serif-display text-[32px] md:text-[48px] font-medium text-center mb-14">
            Customers See Results In Just 2-8 Weeks!
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <img src={LIFESTYLE.resultsImg} alt="Results" className="w-full rounded-sm object-cover" />
            <div className="space-y-10">
              {STATS.map((s) => (
                <div key={s.label} className="flex items-baseline gap-5 border-b border-[#1A1A1A]/12 pb-6">
                  <span className="font-serif-display text-[52px] md:text-[64px] leading-none font-medium">{s.value}</span>
                  <span className="text-[14px] text-[#1A1A1A]/65 leading-snug">{s.label}</span>
                </div>
              ))}
              <p className="text-[11px] text-[#1A1A1A]/45 leading-relaxed">
                &dagger; Claims are based on an independent consumer perception study and an expert assessment by a dermatologist in an independent clinical study of 32 people after using the Red Light Therapy Mask 5x per week within an 8-week period.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CINEMATIC VIDEO BAND */}
      <section className="relative h-[420px] md:h-[600px] overflow-hidden bg-[#1A1A1A]">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster={PRODUCT.images[5]}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/25" />
        <div className="relative h-full max-w-[1280px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-12 md:pb-16">
          <p className="text-[11px] tracking-[0.3em] uppercase text-white/80 mb-3">See It In Action</p>
          <h2 className="font-serif-display text-[32px] md:text-[52px] leading-[1.05] font-medium text-white max-w-2xl">
            15 Minutes to Your Best Skin
          </h2>
          <p className="text-[14px] md:text-[16px] text-white/85 max-w-md mt-4">
            Wireless, comfortable, and dermatologist-designed &mdash; slip it on, relax, and let the light do the work.
          </p>
        </div>
      </section>

      {/* POWERFUL SCIENCE */}
      <section className="py-20 md:py-28 bg-[#EDE6DC]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">Dermatologist recommended</p>
            <h2 className="font-serif-display text-[34px] md:text-[48px] leading-[1.05] font-medium mb-4">
              The Powerful Science <span className="italic">Behind the Mask.</span>
            </h2>
            <p className="text-[15px] text-[#1A1A1A]/70 mb-9">Red light therapy is PROVEN to reduce signs of visible aging!</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              {SCIENCE.map((s) => (
                <div key={s.title}>
                  <h4 className="text-[15px] font-medium mb-1.5">{s.title}</h4>
                  <p className="text-[13px] text-[#1A1A1A]/60 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <img src={LIFESTYLE.science} alt="Science behind the mask" className="w-full rounded-sm object-cover" />
        </div>
      </section>

      {/* ENJOY GLOW UP */}
      <section className="py-20 md:py-28 bg-[#EDE6DC]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <img src={LIFESTYLE.spa} alt="Enjoy your glow up" className="w-full rounded-sm object-cover" />
          <div>
            <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">Relax babe. We got it from here.</p>
            <h2 className="font-serif-display text-[34px] md:text-[48px] leading-[1.05] font-medium mb-5">
              Enjoy your <span className="italic">Glow Up</span>
            </h2>
            <p className="text-[15px] text-[#1A1A1A]/70 mb-8 max-w-md">
              Your road to healthier, more radiant looking skin is literally just as simple as an at-home spa day.
            </p>
            <div className="space-y-6 mb-9">
              {FEATURE_BENEFITS.slice(0, 3).map((f) => (
                <div key={f.title} className="flex gap-4">
                  <div className="mt-0.5 h-6 w-6 rounded-full bg-[#1A1A1A] text-[#F4EFE8] flex items-center justify-center flex-shrink-0">
                    <Check size={13} />
                  </div>
                  <div>
                    <h4 className="text-[15px] font-medium">{f.title}</h4>
                    <p className="text-[13px] text-[#1A1A1A]/60 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleAdd} className="btn-lux group px-9 py-4 text-[12px] tracking-[0.22em] uppercase gap-2">
              Get yours now! <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="font-serif-display text-[30px] md:text-[42px] font-medium mb-10 text-center">Frequently Asked Questions</h2>
          <div className="divide-y divide-[#1A1A1A]/12 border-y border-[#1A1A1A]/12">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-[16px] md:text-[18px] font-serif-display font-medium pr-4">{f.q}</span>
                  <Plus size={18} className="flex-shrink-0 transition-transform group-open:rotate-45" />
                </summary>
                <p className="text-[14px] text-[#1A1A1A]/65 leading-relaxed mt-4">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Product;
